/**
 * LLM provider metrics — summary figures, two trend charts and a per-provider breakdown.
 *
 * Rendered by both the dashboard and the LLM metrics page, so it owns no page chrome of its own:
 * every block is a `FormSection` that slots into whatever page includes it.
 *
 * recharts stays — `@coordio/ui` has no charting layer — but every colour now comes from
 * {@link useChartTokens} instead of the hex literals that used to be typed inline. See that hook
 * for why the values are read from the cascade rather than declared in TS.
 */

import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { EmptyState, FormSection, LoadingSkeleton, SimpleTable } from '@coordio/ui/layout';

import type { LlmCallMetricsDto, LlmProviderBreakdown } from '../../types/llmMetrics';
import { chartAxisTick, chartTooltipStyle, useChartTokens } from './useChartTokens';

import './dashboardWidgets.scss';

interface LlmMetricsChartsProps {
  data: LlmCallMetricsDto | null;
  loading: boolean;
}

/** Above this failure rate the headline figure is coloured as an error rather than a success. */
const FAILURE_RATE_ALERT_THRESHOLD = 0.05;

const shortDate = (value: string) =>
  new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

const PROVIDER_COLUMNS = [
  { id: 'provider', header: 'Provider', cell: (row: LlmProviderBreakdown) => row.providerName },
  { id: 'model', header: 'Model', cell: (row: LlmProviderBreakdown) => row.modelName },
  { id: 'calls', header: 'Total calls', cell: (row: LlmProviderBreakdown) => row.totalCalls.toLocaleString() },
  { id: 'failed', header: 'Failed', cell: (row: LlmProviderBreakdown) => row.failedCalls.toLocaleString() },
  {
    id: 'failure-rate',
    header: 'Failure rate',
    cell: (row: LlmProviderBreakdown) => `${(row.failureRate * 100).toFixed(2)}%`,
  },
  {
    id: 'avg-response',
    header: 'Avg response',
    cell: (row: LlmProviderBreakdown) => `${row.averageResponseTimeMs.toFixed(0)}ms`,
  },
];

export const LlmMetricsCharts = ({ data, loading }: LlmMetricsChartsProps) => {
  const tokens = useChartTokens();

  if (loading) {
    return (
      <FormSection title="LLM provider metrics" description="Calls, failure rate and response times by provider.">
        <LoadingSkeleton rows={4} />
      </FormSection>
    );
  }

  // No response yet (or no tenant selected) — the including page decides what to show instead.
  if (!data) return null;

  const hasSeries = data.timeSeries.length > 0;
  const hasBreakdown = data.providerBreakdown.length > 0;

  if (!hasSeries && !hasBreakdown && data.totalCalls === 0) {
    return (
      <FormSection title="LLM provider metrics" description="Calls, failure rate and response times by provider.">
        <EmptyState
          title="No LLM activity in this range"
          description="Once agents start calling a configured provider, their calls, failures and response times appear here."
        />
      </FormSection>
    );
  }

  const failureRateIsHigh = data.overallFailureRate > FAILURE_RATE_ALERT_THRESHOLD;

  const summary = [
    { key: 'total-calls', label: 'Total LLM calls', value: data.totalCalls.toLocaleString(), modifier: '' },
    {
      key: 'failure-rate',
      label: 'Failure rate',
      value: `${(data.overallFailureRate * 100).toFixed(2)}%`,
      modifier: failureRateIsHigh ? 'metric-tile__value--error' : 'metric-tile__value--success',
    },
    {
      key: 'avg-response',
      label: 'Avg response time',
      value: `${data.averageResponseTimeMs.toFixed(0)}ms`,
      modifier: '',
    },
    {
      key: 'p95-response',
      label: 'P95 response time',
      value: `${data.p95ResponseTimeMs.toFixed(0)}ms`,
      modifier: '',
    },
  ];

  return (
    <div className="llm-metrics-charts" data-testid="llm-metrics-charts">
      <FormSection title="LLM provider metrics" description="Calls, failure rate and response times by provider.">
        <div className="llm-metrics-charts__summary">
          {summary.map((tile) => (
            <div className="metric-tile" key={tile.key}>
              <span className="metric-tile__label">{tile.label}</span>
              <span className={`metric-tile__value ${tile.modifier}`.trim()} data-testid={`llm-metric-${tile.key}`}>
                {tile.value}
              </span>
            </div>
          ))}
        </div>
      </FormSection>

      {hasSeries && (
        <div className="llm-metrics-charts__charts">
          <FormSection title="Failure rate over time">
            <div className="llm-metrics-charts__plot">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data.timeSeries}>
                  <defs>
                    {/*
                      The area fade uses the same semantic token as the stroke, so the chart follows
                      the theme without a second colour being declared anywhere.
                    */}
                    <linearGradient id="llmFailureRateGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={tokens.error} stopOpacity={0.3} />
                      <stop offset="100%" stopColor={tokens.error} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={tokens.grid} />
                  <XAxis
                    dataKey="date"
                    tickFormatter={shortDate}
                    tick={chartAxisTick(tokens)}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tickFormatter={(value: number) => `${(value * 100).toFixed(0)}%`}
                    tick={chartAxisTick(tokens)}
                    axisLine={false}
                    tickLine={false}
                  />
                  <RechartsTooltip
                    formatter={(value) => `${(Number(value) * 100).toFixed(2)}%`}
                    contentStyle={chartTooltipStyle(tokens)}
                  />
                  <Area
                    type="monotone"
                    dataKey="failureRate"
                    stroke={tokens.error}
                    strokeWidth={2}
                    fill="url(#llmFailureRateGradient)"
                    dot={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </FormSection>

          <FormSection title="Average response time over time">
            <div className="llm-metrics-charts__plot">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data.timeSeries}>
                  <defs>
                    <linearGradient id="llmResponseTimeGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={tokens.series[0]} stopOpacity={0.3} />
                      <stop offset="100%" stopColor={tokens.series[0]} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={tokens.grid} />
                  <XAxis
                    dataKey="date"
                    tickFormatter={shortDate}
                    tick={chartAxisTick(tokens)}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tickFormatter={(value: number) => `${value.toFixed(0)}ms`}
                    tick={chartAxisTick(tokens)}
                    axisLine={false}
                    tickLine={false}
                  />
                  <RechartsTooltip
                    formatter={(value) => `${Number(value).toFixed(0)}ms`}
                    contentStyle={chartTooltipStyle(tokens)}
                  />
                  <Area
                    type="monotone"
                    dataKey="averageResponseTimeMs"
                    stroke={tokens.series[0]}
                    strokeWidth={2}
                    fill="url(#llmResponseTimeGradient)"
                    dot={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </FormSection>
        </div>
      )}

      {hasBreakdown && (
        <FormSection title="Provider breakdown">
          <div className="llm-metrics-charts__table">
            <SimpleTable
              caption="LLM calls by provider and model"
              columns={PROVIDER_COLUMNS}
              rows={data.providerBreakdown}
              getRowKey={(row) => `${row.providerName}-${row.modelName}`}
            />
          </div>
        </FormSection>
      )}
    </div>
  );
};
